import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Input } from "@/components/ui/Input"
import { Button } from "@/components/ui/Button"
import {
  warehouse_channel_products_path,
  products_warehouse_channel_products_path,
} from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"

interface ProductResult {
  external_id: string
  name: string
}

interface Props {
  orgNodeId: string
  warehouseId: string
  existingExternalIds: Set<string>
  onClose: () => void
}

const DEBOUNCE_MS = 300

export default function AddProductDialog({ orgNodeId, warehouseId, existingExternalIds, onClose }: Props) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<ProductResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<ProductResult | null>(null)
  const [selling, setSelling] = useState(true)
  const [gifting, setGifting] = useState(false)
  const requestId = useRef(0)
  const market = useMarket()

  useEffect(() => {
    const id = ++requestId.current
    setLoading(true)
    const timer = setTimeout(() => {
      const url = `${products_warehouse_channel_products_path(market.slug)}?q=${encodeURIComponent(query)}`
      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((json: ProductResult[]) => {
          if (id === requestId.current) setResults(json)
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false)
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  function add() {
    if (!selected) return
    router.post(
      warehouse_channel_products_path(market.slug),
      {
        warehouse_channel_product: {
          org_node_id: orgNodeId,
          warehouse_id: warehouseId,
          product_external_id: selected.external_id,
          selling,
          gifting,
        },
      },
      { onSuccess: onClose }
    )
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="w-[420px] max-w-[90vw] bg-background rounded-lg shadow-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Add product</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-6 w-6 text-lg leading-none text-muted-foreground hover:text-muted-foreground"
          >
            ×
          </Button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <Input
            autoFocus
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            {loading && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                Searching…
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                No matching products.
              </div>
            )}
            {!loading &&
              results.map((p) => {
                const added = existingExternalIds.has(p.external_id)
                const isSelected = selected?.external_id === p.external_id
                return (
                  <Button
                    key={p.external_id}
                    type="button"
                    variant="ghost"
                    disabled={added}
                    onClick={() => setSelected(p)}
                    className={`h-auto w-full justify-between gap-2 rounded-none border-b border-border px-3 py-2 text-left font-normal last:border-b-0 ${
                      isSelected ? "bg-accent" : ""
                    }`}
                  >
                    <span className="truncate text-foreground">{p.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground select-all">
                      {added ? "already added" : `#${p.external_id}`}
                    </span>
                  </Button>
                )
              })}
          </div>
          {selected && (
            <div className="rounded-md border border-border px-3 py-3 space-y-3">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-foreground">{selected.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground select-all">
                  #{selected.external_id}
                </span>
              </div>
              <div className="flex items-center gap-5 text-sm text-foreground">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={selling}
                    onChange={(e) => setSelling(e.target.checked)}
                  />
                  Selling
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    checked={gifting}
                    onChange={(e) => setGifting(e.target.checked)}
                  />
                  Gifting
                </label>
                <Button size="sm" className="ml-auto" onClick={add}>
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
